<template>
  <Overlay
    type="dialog"
    :open="open"
    :closing-blocked="inProgress"
    :style="[isNoShow ? `--sl-panel-background-color: #F5F5F5` : '', '--sl-z-index-dialog: 500']"
    @request-close="close"
  >
    <sl-badge v-if="isNoShow" slot="label" variant="neutral"> No show </sl-badge>
    <div slot="header-actions" class="modify__actions">
      <div class="modify__actionSlot">
        <span
          v-access:appointmentSummary
          class="modify__action modify__action--fade modify__actionSlot__item"
          :class="{ 'modify__action--visible': loadedCustomer && hasOrderHistory }"
          @click="summaryOpen = true"
        >
          <PhSparkle :size="20" />
        </span>
        <sl-tooltip
          class="modify__actionSlot__item"
          :content="$t('appointments.modify.new_customer_tooltip')"
          placement="bottom"
        >
          <span
            class="modify__action modify__action--static modify__action--fade"
            :class="{
              'modify__action--visible': loadedCustomer && customerOrdersCount !== null && !hasOrderHistory
            }"
          >
            <PhUserPlus :size="20" />
          </span>
        </sl-tooltip>
      </div>
      <span v-if="loadedCustomer" class="modify__action" @click="newNoteOpen = true">
        <PhNote :size="20" />
      </span>
      <span v-if="canModifyAppointment" class="modify__action" @click="onChange">
        <PhClockClockwise :size="20" />
      </span>
      <span v-if="canModifyAppointment" class="modify__action" @click="deleteConfirmOpen = true">
        <PhTrash :size="20" />
      </span>
      <span
        v-if="!canModifyAppointment && !props.appointment.noShow"
        class="modify__action"
        @click="noShowConfirmOpen = true"
      >
        <PhGhost :size="20" />
      </span>
    </div>
    <div v-if="props.appointment?.id" class="modify__content">
      <div class="modify__section">
        <PhCalendarCheck :size="20" class="modify__section__icon" />
        <div
          :class="{
            'no-show-text': isNoShow
          }"
        >
          <h3 class="modify__title">
            {{ title }}
          </h3>
          <div class="modify__section__rows">
            <div>{{ formattedDate }} • {{ formattedTime }}</div>
            <div style="display: flex; gap: 8px; flex-wrap: wrap">
              {{ props.currentCompany.externalId }}
              <span v-for="employee in employees" :key="employee.name">
                {{ employee.name }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <div v-if="customerName" class="modify__section">
        <PhUser :size="20" class="modify__section__icon" />
        <div class="modify__section__content">
          <div
            :class="{
              'no-show-text': isNoShow
            }"
            class="modify__user__person"
          >
            {{ customerName }}
            <CustomerFlags :items="loadedCustomer?.tags || []" />
          </div>
          <div
            class="modify__user__contact modify__section__rows"
            :class="{
              'no-show-text': isNoShow
            }"
          >
            <div>{{ customerEmail }}</div>
            <div>{{ customerPhone }}</div>
          </div>
          <div v-if="customerName" class="modify__user__load">
            <sl-button
              variant="default"
              size="small"
              :disabled="!loadedCustomer"
              @click="onCustomerClick"
            >
              {{ $t('appointments.modify.load_customer') }} </sl-button
            >&nbsp;
            <sl-button
              variant="default"
              size="small"
              :disabled="!loadedCustomer || loadingCart"
              :loading="loadingCart"
              @click="onLoadLastCart"
            >
              {{ $t('appointments.modify.load_last_cart') }}
            </sl-button>
          </div>
          <div v-if="hasAlerts" class="modify__user__alerts">
            <sl-alert v-if="hasAlertNoCustomer" variant="danger" open>
              {{ $t('appointments.modify.customer_not_found') }}
            </sl-alert>
          </div>
        </div>
      </div>
      <div v-if="product" class="modify__section">
        <PhEyeglasses v-if="product.type === 'frame'" :size="20" class="modify__section__icon" />
        <PhSunglasses v-else-if="product.type === 'sun'" :size="20" class="modify__section__icon" />
        <PhHandbag v-if="product.type === 'accessory'" :size="20" class="modify__section__icon" />
        <div class="modify__section__content modify__section__product">
          <div style="margin-bottom: 8px">{{ $t('appointments.modify.product.description') }}</div>
          <ProductCardMini :item="product" />
        </div>
      </div>
      <div v-if="customerName" class="modify__section">
        <PhNote :size="20" class="modify__section__icon" />
        <sl-details
          class="modify__section__content modify__section__notes"
          :class="{
            'modify__section__notes--noshow': isNoShow
          }"
        >
          <span slot="summary" style="display: flex; align-items: center">
            {{ $t('appointments.modify.notes.title') }}
            (<template v-if="notesInProgress">
              <sl-spinner style="font-size: 0.8rem"></sl-spinner>
            </template>
            <template v-else>
              {{ notes.length }} </template
            >)
          </span>
          <div class="modify__notes">
            <template v-if="notesInProgress">
              <sl-spinner></sl-spinner>
            </template>
            <template v-else-if="notes.length === 0">
              <span style="color: grey">
                {{ $t('appointments.modify.notes.empty') }}
              </span>
            </template>
            <template v-else>
              <TransitionGroup name="notes">
                <AppointmentNote v-for="note in notes" :key="note.description" :note="note" />
              </TransitionGroup>
            </template>
          </div>
        </sl-details>
      </div>
    </div>
  </Overlay>
  <Overlay type="dialog" :open="deleteConfirmOpen" @request-close="onAppointmentDeleteCancel">
    <h4 class="modify__cancel__title">
      {{ $t('appointments.modify.cancel_confirm.title') }}
    </h4>
    <p v-if="props.appointment.customers?.length > 0">
      {{ $t('appointments.modify.cancel_confirm.description') }}
    </p>
    <div slot="footer" style="display: flex; width: 100%; justify-content: space-between">
      <sl-button variant="default" @click="onAppointmentDeleteCancel">
        {{ $t('globals.cancel') }}
      </sl-button>
      <sl-button
        variant="primary"
        :disabled="deleteInProgress"
        :loading="deleteInProgress"
        @click="onAppointmentDeleteConfirm"
      >
        {{ $t('appointments.modify.cancel_confirm.confirm') }}
      </sl-button>
    </div>
  </Overlay>
  <Overlay type="dialog" :open="noShowConfirmOpen" @request-close="onNoShowCancel">
    <h4 class="modify__cancel__title">
      {{ $t('appointments.modify.no_show_confirm.title') }}
    </h4>
    <p>
      {{ $t('appointments.modify.no_show_confirm.description') }}
    </p>
    <div slot="footer" style="display: flex; width: 100%; justify-content: space-between">
      <sl-button variant="default" @click="onNoShowCancel">
        {{ $t('globals.cancel') }}
      </sl-button>
      <sl-button
        variant="primary"
        :disabled="noShowInProgress"
        :loading="noShowInProgress"
        @click="onNoShowConfirm"
      >
        {{ $t('appointments.modify.no_show_confirm.confirm') }}
      </sl-button>
    </div>
  </Overlay>
  <Overlay
    type="dialog"
    :open="newNoteOpen"
    :label="$t('appointments.modify.new_note.title')"
    style="--width: 600px"
    @request-close="onNewNoteCancel"
  >
    <div class="note">
      <p class="note__description">
        {{ $t('appointments.modify.new_note.description') }}
      </p>
      <div class="note__content">
        <div class="note__user">
          <PhUser :size="20" class="note__user__icon" />
          <span class="note__user__name">
            {{ customerName }}
            <CustomerFlags :items="loadedCustomer?.tags || []" />
          </span>
        </div>
        <sl-textarea
          v-model="newNote"
          :placeholder="$t('appointments.modify.new_note.text.placeholder')"
        ></sl-textarea>
      </div>
    </div>
    <div slot="footer" style="display: flex; width: 100%; justify-content: space-between">
      <sl-button variant="default" @click="onNewNoteCancel">
        {{ $t('globals.cancel') }}
      </sl-button>
      <sl-button
        variant="primary"
        :disabled="newNoteInProgress || !newNote"
        :loading="newNoteInProgress"
        @click="onNewNoteConfirm"
      >
        {{ $t('globals.save') }}
      </sl-button>
    </div>
  </Overlay>
  <CustomerSummary
    v-model:open="summaryOpen"
    :customer-id="clCustomer?.id"
    :customer-email="clCustomer?.email"
  />
</template>

<script setup>
import '@shoelace-style/shoelace/dist/components/details/details'
import '@shoelace-style/shoelace/dist/components/textarea/textarea'
import '@shoelace-style/shoelace/dist/components/tooltip/tooltip'
import '@shoelace-style/shoelace/dist/components/spinner/spinner'

import { computed, inject, ref, watch } from 'vue'
import { format } from 'date-fns'
import {
  PhGhost,
  PhNote,
  PhClockClockwise,
  PhTrash,
  PhCalendarCheck,
  PhUser,
  PhEyeglasses,
  PhSunglasses,
  PhHandbag,
  PhSparkle,
  PhUserPlus
} from '@phosphor-icons/vue'

import Overlay from '@/components/shared/Overlay/Overlay.vue'
import CustomerFlags from '@/components/shared/CustomerFlags/CustomerFlags.vue'
import AppointmentNote from '@/components/shared/AppointmentNote/AppointmentNote.vue'
import ProductCardMini from '@/components/Product/ProductCardMini.vue'
import CustomerSummary from '@/components/Customer/CustomerSummary/CustomerSummary.vue'

import { useAppointmentsStore } from '@/stores/appointments'
import { useTimify } from '@/composables/useTimify.js'
import { useTypesense } from '@/composables/useTypesense.js'
import { useI18n } from 'vue-i18n'
import { useNotification } from '@/composables/useNotification.js'
import { useCustomer } from '@/composables/useCustomer.js'
import { useCartStore } from '@/stores/cart.js'
import { useCustomerStore } from '@/stores/customer.js'
import { useUiStore } from '@/stores/ui.js'
import { useNotes } from '@/composables/useNotes.js'
import { useDeviceStore } from '@/stores/device.js'

import * as Sentry from '@sentry/vue'

const emit = defineEmits(['changed', 'edit'])

const props = defineProps({
  appointment: {
    type: Object,
    default() {
      return {}
    }
  },
  currentCompany: {
    type: Object,
    default() {
      return {}
    }
  }
})

const cl = inject('cl')
const { locale, t } = useI18n()
const timify = useTimify()
const notification = useNotification()
const { loadCustomerByEmail } = useCustomer()
const customerStore = useCustomerStore()
const cartStore = useCartStore()
const uiStore = useUiStore()
const deviceStore = useDeviceStore()
const { fetchCustomerNotes, createCustomerNote } = useNotes()
const typesense = useTypesense()
const appointmentsStore = useAppointmentsStore()

const currentLocale = computed(() => {
  let currentLocale = locale.value
  if (currentLocale.includes('de')) {
    currentLocale = 'de-de'
  } else if (currentLocale.includes('en')) {
    currentLocale = 'en-gb'
  } else if (currentLocale.includes('fr')) {
    currentLocale = 'fr-fr'
  }

  return currentLocale
})

const open = defineModel('open', {
  type: Boolean,
  default: false
})

function close() {
  open.value = false
}

const clCustomer = computed(() => {
  return props.appointment.clCustomer
})
const timifyCustomer = computed(() => {
  return props.appointment?.singleCustomers?.[0]
})
const customerEmail = computed(() => {
  return timifyCustomer.value?.email
})
const customerPhone = computed(() => {
  return timifyCustomer.value?.phone?.phone
})
const customerName = computed(() => {
  return timifyCustomer.value?.fullName
})
const employees = computed(() => {
  return (props.appointment?.resources || []).filter((resource) => Boolean(resource.externalId))
})

const isNoShow = computed(() => {
  return props.appointment.noShow
})

const hasAlertNoCustomer = computed(() => {
  return loadedCustomerReady.value && !loadedCustomer.value
})

const hasAlerts = computed(() => {
  return hasAlertNoCustomer.value
})

const title = computed(() => {
  return [
    appointmentsStore.appointmentTitleFixByService(
      props.appointment.serviceKey,
      props.appointment.title
    ),
    props.appointment?.duration
      ? t('appointments.duration_title', { duration: props.appointment.duration })
      : ''
  ]
    .filter(Boolean)
    .join(' ')
})

const formattedDate = computed(() => {
  if (!props.appointment.start) {
    return ''
  }
  return new Intl.DateTimeFormat(currentLocale.value, {
    dateStyle: 'full'
  }).format(props.appointment.start)
})

const formattedTime = computed(() => {
  if (!props.appointment.start) {
    return ''
  }
  return [format(props.appointment.start, 'HH:mm'), format(props.appointment.end, 'HH:mm')]
    .filter(Boolean)
    .join(' – ')
})

// delete
const canModifyAppointment = computed(() => {
  return new Date() < props.appointment.start
})
const deleteInProgress = ref(false)
const inProgress = computed(() => {
  return deleteInProgress.value
})

const deleteConfirmOpen = ref(false)

async function onAppointmentDeleteConfirm() {
  deleteInProgress.value = true
  try {
    await timify.cancel({
      appointmentId: props.appointment.id,
      companyId: props.currentCompany?.id
    })
    emit('changed')
    notification.showNotification({
      message: t('appointments.modify.cancel_confirm.success'),
      variant: 'success'
    })
  } catch {
    notification.showNotification({
      message: t('appointments.modify.cancel_confirm.failure'),
      variant: 'danger'
    })
  } finally {
    deleteInProgress.value = false
    deleteConfirmOpen.value = false
    open.value = false
  }
}

function onAppointmentDeleteCancel() {
  deleteConfirmOpen.value = false
}

function onChange() {
  if (
    props.appointment?.combination?.events?.[0] &&
    props.appointment?.combination?.events?.[0]?.eventId !== props.appointment.id
  ) {
    notification.showNotification({
      message: t('appointments.modify.combination_change_second_appointment_error'),
      variant: 'warning'
    })
  } else {
    emit('edit')
  }
}

// todo move to appointments store
// is done again in app.vue when editing
const loadedCustomer = ref(null)
const loadedCustomerReady = ref(false)
const notesData = ref(undefined)
const loadingCart = ref(false)
const customerOrdersCount = ref(null)

const hasOrderHistory = computed(() => {
  return customerOrdersCount.value > 0
})

const notesInProgress = computed(() => {
  return notesData.value === undefined
})
const notes = computed(() => {
  return [...(notesData.value || [])]
    .filter(Boolean)
    .slice(0, 3)
    .map((note) => {
      const { description = '', created_at, customer_note } = note
      const maxLength = 100
      const truncatedDescription =
        description.length > maxLength - 3 ? `${description.slice(0, maxLength)}...` : description
      return {
        description: truncatedDescription,
        customer_note: Boolean(customer_note),
        created_at: new Date(created_at).toLocaleDateString(currentLocale.value)
      }
    })
})

watch(
  customerEmail,
  async (value) => {
    if (value) {
      loadedCustomer.value = await loadCustomerByEmail(value)
      loadedCustomerReady.value = true
      if (loadedCustomer.value?.id) {
        const customerId = loadedCustomer.value.id
        customerOrdersCount.value = await cl.orders
          .count({
            filters: {
              customer_id_eq: customerId,
              order_copies_status_not_eq_or_null: 'completed',
              status_matches_any: 'placed,approved,cancelled',
              approved_at_or_placed_at_not_null: true
            }
          })
          .catch(() => null)

        fetchCustomerNotes(customerId).then((notesResponse) => {
          if (notesResponse) {
            notesData.value = notesResponse
          }
        })
      }
    } else {
      loadedCustomer.value = null
      loadedCustomerReady.value = false
      customerOrdersCount.value = null
    }
  },
  { immediate: true }
)

async function onCustomerClick() {
  if (loadedCustomer.value) {
    customerStore.setCustomer(loadedCustomer.value)
    //
    const billingAddress = loadedCustomer.value.customer_addresses?.filter(
      (item) => item.address.metadata?.is_default_billing
    )
    const shippingAddress = loadedCustomer.value.customer_addresses?.filter(
      (item) => item.address.metadata?.is_default_shipping
    )
    cartStore.setBillingAddress(billingAddress?.[0]?.address || {})
    cartStore.setShippingAddress(shippingAddress?.[0]?.address || {})
    open.value = false
    uiStore.$patch({ currentCustomerDrawer: 'profile' })
  }
}

async function onLoadLastCart() {
  if (!loadedCustomer.value) return

  loadingCart.value = true
  try {
    // Set customer
    customerStore.setCustomer(loadedCustomer.value)

    // Set addresses
    const billingAddress = loadedCustomer.value.customer_addresses?.filter(
      (item) => item.address.metadata?.is_default_billing
    )
    const shippingAddress = loadedCustomer.value.customer_addresses?.filter(
      (item) => item.address.metadata?.is_default_shipping
    )
    cartStore.setBillingAddress(billingAddress?.[0]?.address || {})
    cartStore.setShippingAddress(shippingAddress?.[0]?.address || {})

    // Fetch the customer's most recent cart
    const carts = await cl.orders.list({
      include: [
        'attachments',
        'line_items.item',
        'line_items.line_item_options.sku_option',
        'market',
        'shipping_address',
        'billing_address',
        'tags'
      ],
      fields: {
        markets: ['code', 'metadata'],
        orders: [
          'attachments',
          'subtotal_amount_float',
          'total_amount_float',
          'currency_code',
          'fulfillment_status',
          'payment_status',
          'status',
          'number',
          'created_at',
          'shipping_address',
          'billing_address',
          'metadata',
          'line_items',
          'market',
          'tags',
          'skus_count',
          'total_amount_with_taxes_float'
        ]
      },
      filters: {
        customer_id_eq: loadedCustomer.value.id,
        status_matches_any: 'draft,pending',
        archived_at_null: true,
        market_merchant_address_country_code_eq: deviceStore.settings.country.toUpperCase()
      },
      sort: { updated_at: 'desc' },
      pageSize: 1
    })

    if (carts.length > 0) {
      customerStore.setCurrentCart(carts[0])
      customerStore.setCarts(carts)
      uiStore.showCart = true
      open.value = false
      notification.showNotification({
        message: t('appointments.modify.cart_loaded'),
        variant: 'success',
        icon: 'cart-check'
      })
    } else {
      notification.showNotification({
        message: t('appointments.modify.no_cart_found'),
        variant: 'warning'
      })
    }
  } catch (error) {
    notification.showNotification({
      message: t('globals.error'),
      variant: 'danger'
    })
  } finally {
    loadingCart.value = false
  }
}

const noShowConfirmOpen = ref(false)
const noShowInProgress = ref(false)
const summaryOpen = ref(false)

function onNoShowCancel() {
  noShowConfirmOpen.value = false
}

async function onNoShowConfirm() {
  noShowInProgress.value = true
  try {
    await timify.change({
      appointmentId: props.appointment.id,
      companyId: props.currentCompany?.id,
      tagIds: ['676172ef90313cbc66dc74b6']
    })
    emit('changed')
    notification.showNotification({
      message: t('appointments.modify.no_show_confirm.success'),
      variant: 'success'
    })
  } catch {
    notification.showNotification({
      message: t('appointments.modify.no_show_confirm.failure'),
      variant: 'danger'
    })
  } finally {
    noShowInProgress.value = false
    noShowConfirmOpen.value = false
    open.value = false
  }
}

// new note
const newNote = ref('')
const newNoteOpen = ref(false)
const newNoteInProgress = ref(false)

function onNewNoteCancel() {
  newNoteOpen.value = false
}

const notesApiOptions = {
  errorNotify: false,
  errorCatch: false
}

async function onNewNoteConfirm() {
  if (!newNote.value || !loadedCustomer.value) {
    return
  }
  newNoteInProgress.value = true
  try {
    await createCustomerNote(loadedCustomer.value.id, newNote.value, {
      ...notesApiOptions,
      metadata: {
        timify_appointment: {
          id: props.appointment.id,
          company_id: appointmentsStore.currentCompany?.id
        }
      }
    })
    try {
      notesData.value = await fetchCustomerNotes(loadedCustomer.value.id)
    } catch {
      notesData.value.unshift({
        description: newNote.value,
        created_at: new Date().toISOString()
      })
    }
    newNoteOpen.value = false
    newNote.value = ''
  } catch {
    notification.showNotification({
      message: t('appointments.modify.new_note.error'),
      variant: 'danger'
    })
  } finally {
    newNoteInProgress.value = false
  }
}

// product
const sku = computed(() => {
  return props.appointment?.metadata?.sku || null
})
const product = ref(null)
watch(
  sku,
  async (value) => {
    if (value) {
      try {
        const searchResults = await typesense
          .client()
          .collections(import.meta.env.VITE_TYPESENSE_PRODUCTS_COLLECTION)
          .documents()
          .search({
            q: value,
            query_by: 'sku',
            limit: 1
          })
        if (searchResults?.hits?.length > 0) {
          product.value = searchResults.hits[0].document
        }
      } catch (error) {
        Sentry.captureException(error)
      }
    } else {
      product.value = null
    }
  },
  { immediate: true }
)
</script>

<style scoped src="./AppointmentsModify.css"></style>
